import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Card, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", mobile: "", address: "", username: "", password: "", license_number: "", vehicle_number: "", status: true };

export default function Drivers() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/drivers").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  function openCreate() { setEditing(null); setForm(EMPTY); setOpen(true); }
  function openEdit(d) { setEditing(d); setForm({ ...EMPTY, ...d, password: "" }); setOpen(true); }
  async function save() {
    try {
      const payload = { ...form };
      if (editing && !payload.password) delete payload.password;
      if (editing) await api.put(`/drivers/${editing.id}`, payload);
      else await api.post("/drivers", payload);
      toast.success(editing ? "Driver updated" : "Driver created");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }
  async function remove(d) {
    if (!window.confirm(`Delete ${d.name}?`)) return;
    await api.delete(`/drivers/${d.id}`);
    toast.success("Deleted"); load();
  }

  return (
    <div data-testid="drivers-page">
      <PageHeader
        eyebrow="Logistics"
        title="Drivers"
        description="Manage delivery drivers — their login, vehicle, and status."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-cobalt-500 hover:bg-cobalt-700 text-white" data-testid="driver-create-btn"><Plus size={16} className="mr-1"/>New Driver</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Driver" : "New Driver"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name</Label><Input data-testid="driver-name" value={form.name} onChange={e=>setForm({...form, name: e.target.value})}/></div>
                <div><Label>Mobile</Label><Input data-testid="driver-mobile" value={form.mobile} onChange={e=>setForm({...form, mobile: e.target.value})}/></div>
                <div><Label>Username</Label><Input data-testid="driver-username" value={form.username} onChange={e=>setForm({...form, username: e.target.value})}/></div>
                <div><Label>{editing ? "New Password (leave blank)" : "Password"}</Label><Input type="text" data-testid="driver-password" value={form.password} onChange={e=>setForm({...form, password: e.target.value})}/></div>
                <div><Label>License Number</Label><Input value={form.license_number} onChange={e=>setForm({...form, license_number: e.target.value})}/></div>
                <div><Label>Vehicle Number</Label><Input value={form.vehicle_number} onChange={e=>setForm({...form, vehicle_number: e.target.value})}/></div>
                <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address} onChange={e=>setForm({...form, address: e.target.value})}/></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-cobalt-500 hover:bg-cobalt-700" data-testid="driver-save-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card padding="p-0">
        {list.length === 0 ? <div className="p-8"><Empty title="No drivers yet"/></div> : (
          <div className="overflow-x-auto">
            <table className="ddms-table" data-testid="drivers-table">
              <thead><tr><th>Name</th><th>Username</th><th>Mobile</th><th>License</th><th>Vehicle</th><th>Status</th><th className="num">Actions</th></tr></thead>
              <tbody>
                {list.map(d => (
                  <tr key={d.id}>
                    <td className="font-medium">{d.name}</td>
                    <td className="font-mono text-xs">{d.username}</td>
                    <td className="font-mono text-xs">{d.mobile}</td>
                    <td className="font-mono text-xs">{d.license_number || "—"}</td>
                    <td className="font-mono text-xs">{d.vehicle_number || "—"}</td>
                    <td><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${d.status ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{d.status ? "Active" : "Disabled"}</span></td>
                    <td className="num">
                      <Button size="sm" variant="outline" onClick={()=>openEdit(d)} className="mr-2"><Pencil size={12}/></Button>
                      <Button size="sm" variant="outline" onClick={()=>remove(d)} className="text-rose-600 border-rose-200"><Trash2 size={12}/></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
